import { cn } from '@/lib/utils';
import { SliderInput } from '../shared/SliderInput';
import { Tip } from '../shared/constants';
import { UploadProfilePreview } from '../UploadProfilePreview';
import type { HubV2State, ProfileMode } from '../hooks/useHubV2State';

export default function StepGrid({ state }: { state: HubV2State }) {
  const {
    gMax, setGMax, buP, setBuP, gridMode, setGridMode,
    blokstroomEnabled, setBlokstroomEnabled, blokstroomPower, setBlokstroomPower,
    blokstroomStart, setBlokstroomStart, blokstroomEnd, setBlokstroomEnd,
    uploadedProfiles, uploadErrors, uploadLoading, handleGridUpload, clearGrid,
    r,
  } = state;

  return (
    <div className="space-y-4">
      <div className="p-5 bg-white rounded-xl border border-[#D9E2EC] shadow-sm">
        <div className="text-sm font-bold text-[#0B1F4A] mb-1">⚡ Grid & Backup</div>
        <div className="text-xs text-[#8899AA] mb-4">Configureer de netaansluiting, backup generator en blokstroom.</div>

        {/* Grid mode selector */}
        <div className="flex items-center gap-1.5 mb-3 p-2 rounded-lg bg-[#F0FDF4] border border-[#BBF7D0]">
          {([
            ['standaard', '📊 Standaard', 'Grid & backup handmatig instellen'],
            ['upload', '📂 Upload', 'Verbruiksprofiel importeren (Piekvermogen)'],
          ] as [ProfileMode, string, string][]).map(([mode, label, tip]) => (
            <Tip key={mode} text={tip}>
              <button onClick={() => setGridMode(mode)}
                className={cn(
                  "px-3 py-1.5 text-[10px] font-semibold rounded-md border cursor-pointer transition-all",
                  gridMode === mode
                    ? "bg-[#0B1F4A] text-white border-[#0B1F4A]"
                    : "bg-white text-[#3D4F6F] border-[#D9E2EC] hover:bg-[#F5F8FB]"
                )}>
                {label}
              </button>
            </Tip>
          ))}
          <span className="ml-auto text-xs font-bold text-[#0B1F4A] bg-white px-2 py-0.5 rounded-md border border-[#D9E2EC] tabular-nums">
            {gMax} MW
          </span>
        </div>

        {/* Standaard mode */}
        {gridMode === 'standaard' && (
          <>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2">
              <SliderInput label="Grid Max" value={gMax} set={setGMax} min={0} max={5} step={0.01} unit="MW" group="grid" tooltip="Maximale netaansluiting" />
              <SliderInput label="Backup Vermogen" value={buP} set={setBuP} min={0} max={10} step={0.5} unit="MW" group="back" tooltip="Maximaal vermogen noodgenerator" />
            </div>

            {/* Blokstroom */}
            <div className="mt-3 rounded-lg border border-[#D9E2EC] bg-[#F5F8FB] p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold text-[#0B1F4A]">⚡ Blokstroom (nacht-transportrecht)</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={blokstroomEnabled} onChange={() => setBlokstroomEnabled(!blokstroomEnabled)}
                    className="w-3.5 h-3.5 rounded border-[#D9E2EC] accent-[#00A3C4] cursor-pointer" />
                  <span className="text-[10px] text-[#8899AA]">{blokstroomEnabled ? 'Actief' : 'Uit'}</span>
                </label>
              </div>
              {blokstroomEnabled && (
                <>
                  <div className="mb-2 text-[10px] text-muted-foreground bg-white/60 rounded-md border border-border px-2.5 py-1.5 leading-relaxed">
                    Blokstroom is extra netcapaciteit tijdens daluren. De batterij wordt proactief geladen tegen een lager tarief.
                  </div>
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2">
                    <SliderInput label="Extra Vermogen" value={blokstroomPower} set={setBlokstroomPower} min={0} max={10} step={0.1} unit="MW" group="grid" tooltip="Extra netcapaciteit tijdens blokstroom-uren" />
                    <SliderInput label="Start uur" value={blokstroomStart} set={setBlokstroomStart} min={0} max={23} step={1} unit="h" group="grid" tooltip="Begin blokstroom-venster" />
                    <SliderInput label="Eind uur" value={blokstroomEnd} set={setBlokstroomEnd} min={0} max={23} step={1} unit="h" group="grid" tooltip="Einde blokstroom-venster" />
                  </div>
                  {r.blokstroomImport > 0 && (
                    <div className="mt-2 text-[10px] text-[#3D4F6F] bg-white rounded-md border border-[#D9E2EC] px-2.5 py-1.5">
                      ⚡ Blokstroom import: <strong>{r.blokstroomImport.toFixed(0)} MWh/jaar</strong> ({(r.blokstroomImport / (r.gi || 1) * 100).toFixed(1)}% van totale grid import)
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {/* Upload mode */}
        {gridMode === 'upload' && (
          <div className="space-y-3">
            <div className="text-[10px] text-muted-foreground bg-muted/50 rounded-md border border-border px-3 py-2 leading-relaxed">
              📂 Upload een CSV of Excel bestand met kwartierwaarden om het grid-verbruiksprofiel te bepalen.
            </div>
            {!uploadedProfiles.gridProfile ? (
              <div>
                <input type="file" accept=".csv,.txt,.xlsx,.xls" id="hub-v2-grid-upload" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleGridUpload(f); e.target.value = ''; }} />
                <button onClick={() => document.getElementById('hub-v2-grid-upload')?.click()}
                  disabled={uploadLoading.grid}
                  className="px-4 py-2 text-[11px] font-semibold rounded-lg border border-[#D9E2EC] bg-white text-[#0B1F4A] hover:bg-[#F5F8FB] cursor-pointer transition-colors disabled:opacity-50">
                  {uploadLoading.grid ? '⏳ Verwerken...' : '📂 Kies bestand'}
                </button>
              </div>
            ) : (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-bold text-foreground">✅ Grid profiel geladen</span>
                  <button onClick={clearGrid} className="text-[10px] text-destructive hover:text-destructive/80 cursor-pointer">✕ Verwijderen</button>
                </div>
                {uploadedProfiles.gridStats && (
                  <div className="text-[10px] text-muted-foreground space-y-0.5">
                    <div>📊 <strong>{uploadedProfiles.gridStats.rows.toLocaleString('nl-NL')}</strong> datapunten</div>
                    <div>⚡ Jaarverbruik: <strong className="text-foreground">{uploadedProfiles.gridDemandMwh?.toFixed(0)} MWh</strong></div>
                    {/* Wat het bestand met de simulatie doet, staat er nu bij. Tot
                        8 september 2026 toonde deze kaart alleen datapunten en
                        jaarverbruik, terwijl de netaansluiting onveranderd op de
                        oude handmatige waarde bleef staan. */}
                    {uploadedProfiles.gridPeakKw ? (
                      <div>
                        🔌 Piek <strong className="text-foreground">{(uploadedProfiles.gridPeakKw / 1000).toFixed(2)} MW</strong>
                        {' '}— dit is de netaansluiting geworden
                      </div>
                    ) : (
                      <div className="text-destructive">
                        🔌 Geen piek uit dit bestand te halen; de netaansluiting staat nog op {gMax} MW
                      </div>
                    )}
                  </div>
                )}
                {uploadedProfiles.gridProfile && (
                  <UploadProfilePreview data={uploadedProfiles.gridProfile} label="24-uurs gridprofiel (relatief)" color="hsl(var(--chart-2))" />
                )}
              </div>
            )}
            {uploadErrors.grid && (
              <div className="text-[10px] text-destructive bg-destructive/10 rounded-lg px-3 py-2 border border-destructive/20">❌ {uploadErrors.grid}</div>
            )}
            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2">
              <SliderInput label="Grid Max" value={gMax} set={setGMax} min={0} max={5} step={0.01} unit="MW" group="grid" tooltip="Maximale netaansluiting" />
              <SliderInput label="Backup Vermogen" value={buP} set={setBuP} min={0} max={10} step={0.5} unit="MW" group="back" tooltip="Maximaal vermogen noodgenerator" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
